/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/ContributorDetails/utils/validateContributorDetailsForm.ts.
 */

import type {
  FormDataResponse,
  FormField,
  FormSubSection,
} from "../../../../../../interfaces/formInterfaces";
import {
  type ContributorFormErrors,
  getFirstFormError,
} from "../../shared/contributorFormValidation";

export { getFirstFormError };

function isBackgroundOther(
  backgroundValue: string,
  allFields: FormField[]
): boolean {
  const backgroundValueLower = backgroundValue.toLowerCase().trim();
  if (
    backgroundValueLower === "other" ||
    backgroundValueLower.includes("other")
  ) {
    return true;
  }

  const backgroundField = allFields.find((f) => f.name === "background");
  if (backgroundField?.type === "DROPDOWN") {
    const options = backgroundField.dropdown?.options || [];
    const selectedOption = options.find(
      (opt) =>
        String(opt.value).toLowerCase() === backgroundValueLower ||
        String(opt.label).toLowerCase() === backgroundValueLower
    );
    if (selectedOption) {
      const labelLower = String(selectedOption.label || "").toLowerCase();
      return labelLower.includes("other");
    }
  }

  return false;
}

function getAllFields(formData: FormDataResponse): FormField[] {
  return [
    ...(formData.fields || []),
    ...(formData.ungroupedFields || []),
    ...(formData.subSections?.flatMap((s: FormSubSection) => s.fields) || []),
  ];
}

/** Exported function — validate Contributor Details Form. */
export function validateContributorDetailsForm(
  formData: FormDataResponse,
  values: Record<string, string>
): ContributorFormErrors {
  const errors: ContributorFormErrors = {};
  const allFields = getAllFields(formData);

  allFields.forEach((field) => {
    if (!field.isEnabled) return;

    const disabled =
      field.type === "READONLY" ||
      field.isEditable === false ||
      ["name", "email_address", "Contributor_ID"].includes(field.name);

    if (disabled) return;

    if (field.name === "background_other") {
      const isOther = isBackgroundOther(values.background || "", allFields);
      if (isOther && field.isMandatory && !values[field.name]?.trim()) {
        errors[field.name] = `${field.label} is required when Background is "Other"`;
      }
      return;
    }

    if (field.isMandatory && !values[field.name]?.trim()) {
      errors[field.name] = `${field.label} is required`;
    }
  });

  return errors;
}
